import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'hooks/useTranslation';
import Skeleton from 'components/Skeleton';
import { PAYPAL_LINK_ACCOUNT_SCREEN_KEYS } from '../translationKeys';
import { usePayPalSDK } from 'containers/Payment/PayPal/hooks';
import { getPayPalSetupToken } from 'services/api/payway/paypalSetupToken';
import { useBackNavigationStep } from '../../BackNavigation';
import { PayPalButton } from '../components/PayPalButton';
import './PayPalLinkAccountScreen.scss';

// Copia de containers/Payment/PayPal/screens/PayPalLinkAccountScreen.js.
// Diferencias: el paso lo gobierna el estado local de PayPalScreen (no el
// router), y el AlertModal de error lo monta PayPalScreen para compartirlo
// entre vistas. Tampoco se lee sessionStorage['paypal_vault_error']: esa
// bandera nunca se escribe en el repo (ver docs/context/PayPal-migration.md §5).
const PAYPAL_LOGO_KEY = 'mdp_metodosdepago_agregarmdppaypal_logo_paypal';

export const PayPalLinkAccountScreen = ({
  fromAccountsGrid = false,
  userId,
  userToken,
  transactionParams,
  onVaultSuccess,
  onError,
  onBack,
}) => {
  const { translationMapper } = useTranslation();
  const translatedTexts = useMemo(() => translationMapper(PAYPAL_LINK_ACCOUNT_SCREEN_KEYS), [
    translationMapper,
  ]);
  const assets = useSelector(state => state.asset);
  const paypalLogo = assets?.[PAYPAL_LOGO_KEY] || '';

  // Solo hay back cuando se llegó desde el grid: desde el selector de
  // métodos no hay pantalla previa de PayPal a la cual volver.
  useBackNavigationStep(onBack, { enabled: fromAccountsGrid });

  // El SDK vive aquí y no en el orquestador a propósito: la sesión de vault que
  // devuelve createPayPalSavePaymentSession es de un solo uso y onApprove no la
  // recrea (a diferencia de onCancel, usePayPalSDK.js:375-383). Al montar y
  // desmontar esta pantalla en cada alta, cada vinculación arranca con sesión
  // nueva y se puede agregar una segunda cuenta sin recargar.
  const { isReady, error: sdkError, saveSessionRef, config } =
    usePayPalSDK({
      standalone: true,
      userId,
      userToken,
      transactionParams,
      onVaultSuccess,
    });

  useEffect(
    () => {
      if (sdkError) onError?.();
    },
    [sdkError, onError],
  );

  const handleSetupToken = () =>
    getPayPalSetupToken({
      returnUrl: config?.flows?.vault_token_setup?.return_url,
      cancelUrl: config?.flows?.vault_token_setup?.cancel_url,
      customerId: userId,
      userToken,
    });

  // No solo `isLoading`: ese flag arranca en `false` y usePayPalSDK lo pone en
  // `true` recién dentro de su useEffect, así que el primer render (antes de
  // que el efecto corra) lo tendría en `false` y se vería un frame de
  // contenido real sin botón antes del skeleton. isReady/sdkError sí están
  // correctos desde ese primer render.
  const showSkeleton = !isReady && !sdkError;

  if (showSkeleton) {
    return (
      <div className="paypal-link-account">
        <div className="paypal-link-account__content">
          <div className="paypal-link-account__logo">
            <Skeleton style={{ width: '140px', height: '34px' }} />
          </div>

          <div className="paypal-link-account__texts">
            <div className="paypal-link-account__row paypal-link-account__row--inline">
              <Skeleton style={{ width: '280px', height: '18px' }} />
            </div>
            <div className="paypal-link-account__row">
              <Skeleton
                style={{ width: '220px', height: '18px', marginTop: '8px' }}
              />
            </div>
            <div className="paypal-link-account__row">
              <Skeleton
                style={{ width: '200px', height: '18px', marginTop: '8px' }}
              />
            </div>
          </div>

          <Skeleton
            style={{ width: '256px', height: '48px', borderRadius: '80px' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="paypal-link-account">
      <div className="paypal-link-account__content">
        <div className="paypal-link-account__logo">
          {paypalLogo && (
            <img
              className="paypal-link-account__logo-img"
              src={paypalLogo}
              alt="PayPal"
            />
          )}
        </div>

        <div className="paypal-link-account__texts">
          <div className="paypal-link-account__row paypal-link-account__row--inline">
            <span className="paypal-link-account__text">
              {translatedTexts.TEXT_1}
            </span>{' '}
            <span className="paypal-link-account__text paypal-link-account__text--bold">
              {translatedTexts.TEXT_2}
            </span>
            <p className="paypal-link-account__text">
              {translatedTexts.TEXT_5}
            </p>
          </div>
          <p className="paypal-link-account__text">{translatedTexts.TEXT_3}</p>
          <p className="paypal-link-account__text">{translatedTexts.TEXT_4}</p>
        </div>

        {isReady && (
          <PayPalButton
            saveSessionRef={saveSessionRef}
            onSetupToken={handleSetupToken}
            disabled={!isReady}
            buttonStyle={config?.button_style}
            onError={onError}
          />
        )}
      </div>
    </div>
  );
};

export default PayPalLinkAccountScreen;
